/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getOrDefaultRegionFromResource } from 'src/core/utils/csAccountUtils';
import { getLuisAppService } from 'src/luis/core/services/apps/luisAppService';
import { ConversationPublishSlotStore } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { getPublishService } from 'src/luis/projects/publishPane/services/publishService';

export type PublishHandlerGroup = ReturnType<typeof createPublishSettingsHandlerGroup>;

/**
 * Creates a handler group of functions for the app publish
 * settings to be fired by the dispatcher.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createPublishSettingsHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const logger = getLogger();

    /**
     * Publish an application at either staging or production environment.
     *
     * @param param0.region The region to be publish at.
     * @param param0.isStaging Flag to determines if the publish app for staging or production environment.
     */
    const publishApp = async ({ isStaging, context }: HandlerParams<Pick<ConversationPublishSlotStore, 'isStaging'>>) => {
        const { appId, versionId } = context;
        const appStore = rootStore.luisAppStoreHouse.findById(appId);
        const logTimedActivity = logger.createTimedActivityLogger('PublishApp', () => ({ isStaging }));

        const assignedAccounts = await getLuisAppService({ context }).getAppAzureCsAccounts();

        let region = assignedAccounts.map(a => a.region).join(',');

        if (!region) {
            region = getOrDefaultRegionFromResource(rootStore.azureStore.session.csAccountStore);
        }

        await logTimedActivity(getPublishService({ context }).publish({ region, isStaging }));

        appStore.setPublishSlotMetadata(isStaging, { lastPublishedDate: new Date(), versionId });
    };

    /**
     * Get the publish settings for an application.
     *
     * @param param0.isStaging Flag to determines if the publish app for staging or production environment.
     */
    const getPublishSettings = async ({ isStaging, context }: HandlerParams<Pick<ConversationPublishSlotStore, 'isStaging'>>) => {
        const { appId } = context;
        const appStore = rootStore.luisAppStoreHouse.findById(appId);
        const logTimedActivity = logger.createTimedActivityLogger<ConversationPublishSlotStore>('GetPublishSettings', () => ({
            isStaging
        }));

        const settings = await logTimedActivity(getPublishService({ context }).getPublishSettings({ isStaging }));

        appStore.setPublishSlotMetadata(isStaging, {
            isSentimentAnalysisOn: settings.isSentimentAnalysisOn,
            isSpeechPrimingOn: settings.isSpeechPrimingOn
        });
    };

    /**
     * Updates the publish settings for an application.
     *
     * @param param0.isSentimentAnalysisOn boolean check whether the `sentiment analysis` service in on or not.
     * @param param0.isSpeechPrimingOn boolean check whether the `speech priming` service in on or not.
     * @param param0.isSpellCheckOn boolean check whether the `spell check` service in on or not.
     * @param param0.isStaging boolean check whether the `is staging` slot or not.
     */
    const updatePublishSettings = async ({
        isSentimentAnalysisOn,
        isSpeechPrimingOn,
        isStaging,
        context
    }: HandlerParams<Pick<ConversationPublishSlotStore, 'isSentimentAnalysisOn' | 'isSpeechPrimingOn' | 'isStaging'>>) => {
        const { appId } = context;
        const appStore = rootStore.luisAppStoreHouse.findById(appId);
        const logTimedActivity = logger.createTimedActivityLogger('UpdatePublishSettings', () => ({
            isStaging,
            isSpeechPrimingOn,
            isSentimentAnalysisOn
        }));

        await logTimedActivity(getPublishService({ context }).updatePublishSettings({ isSentimentAnalysisOn, isSpeechPrimingOn }));

        appStore.setPublishSlotMetadata(isStaging, { isStaging, isSentimentAnalysisOn, isSpeechPrimingOn });
    };

    return {
        publishApp,
        getPublishSettings,
        updatePublishSettings
    };
};
